import type { Item } from "@iyk/music-domain"
import type { Link, Show, Tap, Unlock, User, World } from "@iyk/music-domain/client"
import { AppRedirectRoute, AppRoute, AppRouteWithSearchParams } from "@iyk/react-router/client"

export const AppRoutes = {
  home: AppRoute("/"),

  api: {
    users: {
      uploads: {
        createAvatarUpload: AppRoute("/api/users/uploads/avatar")
      }
    },
    clips: {
      uploads: {
        createMediaUpload: AppRoute("/api/clips/uploads/media")
      }
    },
    items: {
      occProducts: {
        requestReleaseDownload: AppRoute("/api/items/occ-products/release-download")
      }
    },
    unlocks: {
      view: AppRoute(`/api/unlocks/view`)
    }
  },

  // Auth
  verifyOtp: AppRedirectRoute("/verify-otp"),
  resendOtp: AppRoute("/resend-otp"),
  login: AppRedirectRoute("/login"),
  logout: AppRoute("/logout"),

  // User
  onboarding: AppRedirectRoute("/onboarding"),
  settings: AppRoute("/settings"),
  myWorlds: AppRoute("/my-worlds"),

  // Links
  externalLinkRedirect: (id: Link.ResourceLink.Id, content: string) =>
    AppRouteWithSearchParams(`/l/:id`, new URLSearchParams({ content }), { id }),

  // Worlds
  worlds: {
    onboarding: AppRoute(`/w/onboarding`),
    home: (slug: World.Slug) => AppRoute(`/w/:slug`, { slug }),
    items: {
      index: (slug: World.Slug) => AppRoute(`/w/:slug/items`, { slug }),
      detail: (slug: World.Slug, id: Item.Id) => AppRoute(`/w/:slug/items/:id`, { slug, id })
    },
    links: (slug: World.Slug) => AppRoute(`/w/:slug/links`, { slug }),
    checkIn: (slug: World.Slug) => AppRoute(`/w/:slug/check-in`, { slug }),
    checkInTroubleshoot: (slug: World.Slug) => AppRoute(`/w/:slug/check-in/troubleshoot`, { slug }),
    proofsClaimed: (slug: World.Slug) => AppRoute(`/w/:slug/proofs/claimed`, { slug }),
    sonoClaimed: (slug: World.Slug) => AppRoute(`/w/:slug/sono/claimed`, { slug }),
    worldId: (slug: World.Slug, username: User.Username) =>
      AppRoute(`/w/:slug/fans/:username/id`, { slug, username }),
    shows: {
      index: (slug: World.Slug) => AppRoute(`/w/:slug/shows`, { slug }),
      detail: (slug: World.Slug, id: Show.Id) => AppRoute(`/w/:slug/shows/:id`, { slug, id }),
      unlocks: (slug: World.Slug, id: Show.Id) => AppRoute(`/w/:slug/shows/:id/unlocks`, { slug, id }),
      sweepstake: (slug: World.Slug, id: Show.Id) => AppRoute(`/w/:slug/shows/:id/sweepstakes`, { slug, id }),
      clipsUpload: (slug: World.Slug, id: Show.Id, returnTo?: string) =>
        AppRouteWithSearchParams(
          `/w/:slug/shows/:id/clips/upload`,
          returnTo ? new URLSearchParams({ returnTo }) : new URLSearchParams(),
          { slug, id }
        )
    },
    unlocks: {
      detail: (slug: World.Slug, id: Unlock.Id) => AppRoute(`/w/:slug/unlocks/:id`, { slug, id }),
      app: (slug: World.Slug, unlockId: Unlock.Id, returnTo?: string) =>
        AppRouteWithSearchParams(
          `/w/:slug/unlocks/apps/:unlockId`,
          returnTo ? new URLSearchParams({ returnTo }) : new URLSearchParams(),
          { slug, unlockId }
        )
    },
    privacyPolicy: (slug: World.Slug) => AppRoute(`/w/:slug/privacy-policy`, { slug }),
    unsubscribe: (slug: World.Slug) => AppRoute("/w/:slug/unsubscribe", { slug })
  },

  tap: (iykTap: Tap.Id) => AppRouteWithSearchParams(`/tap`, new URLSearchParams({ iykTap }))
}

export const withBaseUrl = (appRoute: AppRoute): string => {
  const base = import.meta.env.BASE_URL
  return `${base}${appRoute.replace(/^\//, "")}`
}
